import React, { useState } from "react";
import { Check } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { BillingInterval, SubscriptionPlan } from "@/model/interface/SubscriptionPlan";

const PLAN_META: Record<string, { description: string; popular?: boolean; features: string[] }> = {
  starter: {
    description: "Core CRM, dialer, and SMS for solo investors.",
    features: ["Unlimited contacts and a deal pipeline", "Calling, SMS and voicemail", "Skip tracing, pay per use"],
  },
  pro: {
    description: "Everything, with unlimited seats.",
    popular: true,
    features: ["All features unlocked", "AI voice agents, inbound and outbound", "Call monitoring: listen, whisper, barge"],
  },
  custom: {
    description: "For high-volume teams.",
    features: [
      "Everything in Pro",
      "Negotiated numbers, minutes and texts",
      "Custom usage rates",
      "Add-on pricing for your organisation",
    ],
  },
  "retreevo-special": {
    description: "By invitation. Everything in Pro, billed at our cost.",
    features: [
      "All features unlocked",
      "Plan and add-ons discounted to our cost",
      "Calls, texts and numbers at carrier rates",
    ],
  },
};

const INTERVAL_OPTIONS: { value: BillingInterval; label: string }[] = [
  { value: "MONTHLY", label: "Monthly" },
  { value: "ANNUAL", label: "Annual, 2 months free" },
];

interface PlanOptionsProps {
  plans: SubscriptionPlan[];
  currentPlan?: SubscriptionPlan;
  currentInterval: BillingInterval;
  onChoose: (plan: SubscriptionPlan, interval: BillingInterval) => void;
}

export function PlanOptions({ plans, currentPlan, currentInterval, onChoose }: PlanOptionsProps) {
  const [billingInterval, setBillingInterval] = useState<BillingInterval>(currentInterval);

  const sortedPlans = [...plans].sort((first, second) => first.sortOrder - second.sortOrder);

  const allowanceFeatures = (plan: SubscriptionPlan) => {
    if (plan.slug === "custom" || plan.pricingMode === "AT_COST") {
      return [];
    }
    const features = [
      `${plan.includedOutboundCallMinutes.toLocaleString("en-US")} call minutes a month`,
      `${plan.includedOutboundSms.toLocaleString("en-US")} texts a month`,
    ];
    if (typeof plan.includedPhoneNumbers === "number") {
      features.unshift(`${plan.includedPhoneNumbers.toLocaleString("en-US")} phone numbers`);
    }
    return features;
  };

  const seatLine = (plan: SubscriptionPlan) => {
    if (plan.unlimitedSeats) {
      return "Unlimited seats";
    }
    const seats = `${plan.includedSeats} ${plan.includedSeats === 1 ? "seat" : "seats"} included`;
    return plan.extraSeatPrice > 0 ? `${seats}, +$${plan.extraSeatPrice} per seat` : seats;
  };

  const planAction = (plan: SubscriptionPlan, planInterval: BillingInterval, isPopular: boolean) => {
    if (plan.slug === "custom") {
      return (
        <Button asChild variant="outline" className="w-full">
          <a href="mailto:support@retreevo.com?subject=Custom%20plan">Contact us</a>
        </Button>
      );
    }
    const isCurrent = currentPlan?.slug === plan.slug;
    if (isCurrent && planInterval === currentInterval) {
      return <p className="w-full text-center text-sm font-medium text-muted-foreground">Your current plan</p>;
    }
    if (isCurrent) {
      return (
        <Button variant="outline" className="w-full" onClick={() => onChoose(plan, planInterval)}>
          {planInterval === "ANNUAL" ? "Switch to annual" : "Switch to monthly"}
        </Button>
      );
    }
    return (
      <Button
        variant={!currentPlan && isPopular ? "default" : "outline"}
        className="w-full"
        onClick={() => onChoose(plan, planInterval)}
      >
        {currentPlan ? `Switch to ${plan.name}` : `Choose ${plan.name}`}
      </Button>
    );
  };

  return (
    <section className="space-y-4">
      <div className={cn("flex flex-wrap items-center gap-3", currentPlan ? "justify-between" : "justify-end")}>
        {currentPlan && <h2 className="text-base font-semibold">Change plan</h2>}
        <div className="inline-flex items-center gap-1 rounded-lg border bg-card p-1">
          {INTERVAL_OPTIONS.map((option) => {
            const isActive = option.value === billingInterval;
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={isActive}
                onClick={() => setBillingInterval(option.value)}
                className={cn(
                  "rounded-md border px-3.5 py-1.5 text-sm font-medium",
                  isActive
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-transparent text-muted-foreground hover:bg-muted",
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {sortedPlans.map((plan) => {
          const meta =
            PLAN_META[plan.slug] ??
            (plan.pricingMode === "AT_COST" ? PLAN_META["retreevo-special"] : { description: "", features: [] });
          const isCustom = plan.slug === "custom";
          const isCurrent = currentPlan?.slug === plan.slug;
          const isPopular = !!meta.popular;
          const showsAnnual = billingInterval === "ANNUAL" && !!plan.annualBasePrice;
          const planInterval: BillingInterval = showsAnnual ? "ANNUAL" : "MONTHLY";
          const badge = isCurrent ? "Current plan" : !currentPlan && isPopular ? "Most popular" : null;

          return (
            <Card key={plan.id} className={cn("flex flex-col", badge && "border-primary")}>
              <CardHeader className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-lg">{plan.name}</CardTitle>
                  {badge && (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">{badge}</span>
                  )}
                </div>
                <CardDescription>{meta.description}</CardDescription>
                <div className="pt-3">
                  {isCustom ? (
                    <span className="text-3xl font-bold">Custom</span>
                  ) : plan.pricingMode === "AT_COST" ? (
                    <>
                      <span className="text-3xl font-bold tabular-nums">$0</span>
                      <span className="text-muted-foreground">/month</span>
                      <p className="text-sm text-muted-foreground tabular-nums">
                        ${plan.monthlyBasePrice} list price, 100% off on every invoice
                      </p>
                    </>
                  ) : (
                    <>
                      <span className="text-3xl font-bold tabular-nums">
                        ${showsAnnual ? plan.annualBasePrice : plan.monthlyBasePrice}
                      </span>
                      <span className="text-muted-foreground">/{showsAnnual ? "year" : "month"}</span>
                    </>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{seatLine(plan)}</p>
              </CardHeader>
              <CardContent className="flex-1">
                <ul className="space-y-2">
                  {[...allowanceFeatures(plan), ...meta.features].map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>{planAction(plan, planInterval, isPopular)}</CardFooter>
            </Card>
          );
        })}
      </div>

      <p className="text-sm text-muted-foreground">Prices exclude applicable sales tax.</p>
    </section>
  );
}
